import TurndownService from "turndown";
import { tables as turndownTables } from "turndown-plugin-gfm";
import { marked } from "marked";

/** Pusty akapit TipTap (Enter×2) → znacznik, który przeżywa zapis/odczyt markdown. */
const BLANK_PARAGRAPH_MD = "\n\n<p>&nbsp;</p>\n\n";

const UL_PREFIX =
  /^(?:\u2022|\u2023|\u25E6|\u2043|\u2219|•|●|○|▪|▫|‣|∙|[-*+])\s+/u;
const OL_PREFIX = /^\d+[.)]\s+/;

marked.setOptions({ gfm: true, breaks: true });

function isVisuallyBlankParagraph(node: HTMLElement): boolean {
  if (node.nodeName !== "P") return false;
  const text = (node.textContent || "").replace(/\u00a0/g, " ").replace(/\s+/g, "");
  return text.length === 0;
}

const turndown = new TurndownService({
  headingStyle: "atx",
  codeBlockStyle: "fenced",
  bulletListMarker: "-",
  // Turndown traktuje puste <p> / <p>&nbsp;</p> jako blank i pomija custom rules.
  blankReplacement(_content, node) {
    if (node.nodeName === "P") return BLANK_PARAGRAPH_MD;
    // Turndown dokłada isBlock do Node; typ HTMLElement tego nie ma.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (node as any).isBlock ? "\n\n" : "";
  },
});
turndown.use(turndownTables);
turndown.addRule("iframe", {
  filter: "iframe",
  replacement: (_content, node) => {
    const el = node as HTMLIFrameElement;
    const src = el.getAttribute("src") || "";
    if (!src) return "";
    return `\n\n<iframe src="${src}" frameborder="0" allowfullscreen></iframe>\n\n`;
  },
});
// <p><br></p> nie jest "blank" (void child) - osobna reguła.
turndown.addRule("spacerParagraph", {
  filter: (node) => isVisuallyBlankParagraph(node as HTMLElement),
  replacement: () => BLANK_PARAGRAPH_MD,
});

/** HTML (clipboard / TipTap) -> Markdown. */
export function htmlToMarkdown(html: string): string {
  return turndown.turndown(html || "").trim();
}

/** Czy clipboard HTML ma sensowna strukture (nie sam plain wrap). */
export function clipboardHtmlLooksRich(html: string): boolean {
  const stripped = html.replace(/<!--[\s\S]*?-->/g, "").trim();
  if (!stripped) return false;
  return /<(?:b|strong|i|em|u|a\b|h[1-6]|ul|ol|li|p\b|br|table|tr|td|th|blockquote|pre|code|span\s[^>]*style)/i.test(
    stripped,
  );
}

/** Excel / Sheets: TSV w text/plain -> HTML table (do dalszej konwersji). */
export function tsvToHtmlTable(text: string): string | null {
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
  if (lines.length < 2) return null;
  if (!lines.every((line) => line.includes("\t"))) return null;
  const rows = lines.map((line) => line.split("\t").map((cell) => cell.trim()));
  const cols = Math.max(...rows.map((r) => r.length));
  if (cols < 2) return null;
  const escape = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const pad = (r: string[]) => {
    const out = [...r];
    while (out.length < cols) out.push("");
    return out;
  };
  const [header, ...body] = rows;
  const th = pad(header)
    .map((c) => `<th>${escape(c)}</th>`)
    .join("");
  const trs = body
    .map((r) => `<tr>${pad(r).map((c) => `<td>${escape(c)}</td>`).join("")}</tr>`)
    .join("");
  return `<table><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table>`;
}

function listKindOfLine(text: string): "ul" | "ol" | null {
  const t = text.replace(/\u00a0/g, " ").trim();
  if (!t) return null;
  if (OL_PREFIX.test(t)) return "ol";
  if (UL_PREFIX.test(t)) return "ul";
  return null;
}

function stripListPrefix(text: string): string {
  return text
    .replace(/\u00a0/g, " ")
    .trim()
    .replace(OL_PREFIX, "")
    .replace(UL_PREFIX, "");
}

function escapeHtmlText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Plain text wygląda jak lista (- / • / 1.). */
export function looksLikePlainList(text: string): boolean {
  const nonEmpty = text
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (!nonEmpty.length) return false;
  const listish = nonEmpty.filter((l) => listKindOfLine(l) != null);
  if (listish.length >= 2) return true;
  return listish.length === nonEmpty.length && listish.length >= 1;
}

/** Czy w tekście jest choć jedna linia wypunktowania (• / - / *). */
export function hasBulletLines(text: string): boolean {
  return text
    .replace(/\r\n/g, "\n")
    .split("\n")
    .some((l) => listKindOfLine(l.replace(/\u00a0/g, " ").trim()) === "ul");
}

/** • / ● → `- `, żeby marked zrobił <ul>. */
export function normalizePlainBulletsToMarkdown(text: string): string {
  return text
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => {
      const indent = line.match(/^\s*/)?.[0] ?? "";
      const rest = line.slice(indent.length);
      if (OL_PREFIX.test(rest)) {
        const m = rest.match(/^(\d+)[.)]\s+(.*)$/);
        return m ? `${indent}${m[1]}. ${m[2]}` : line;
      }
      if (UL_PREFIX.test(rest)) {
        return `${indent}- ${stripListPrefix(rest)}`;
      }
      return line;
    })
    .join("\n");
}

/**
 * Mieszany paste (Cursor/Word): nagłówki + akapity + • punkty.
 * Samotne „1. Tytuł” zostaje akapitem; dopiero 2+ kolejne numery → <ol>.
 */
export function plainTextToStructuredHtml(text: string): string | null {
  const raw = text.replace(/\r\n/g, "\n").replace(/\u00a0/g, " ");
  if (!hasBulletLines(raw) && !looksLikePlainList(raw)) return null;

  const lines = raw.split("\n");
  type Seg =
    | { type: "p"; text: string }
    | { type: "ul" | "ol"; items: string[] };
  const segs: Seg[] = [];

  const flushList = (kind: "ul" | "ol", items: string[]) => {
    if (!items.length) return;
    segs.push({ type: kind, items: [...items] });
    items.length = 0;
  };

  let ulItems: string[] = [];
  let olItems: string[] = [];

  const pushParagraph = (line: string) => {
    flushList("ul", ulItems);
    flushList("ol", olItems);
    const t = line.trim();
    if (!t) return;
    segs.push({ type: "p", text: t });
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) {
      flushList("ul", ulItems);
      flushList("ol", olItems);
      continue;
    }
    const kind = listKindOfLine(trimmed);
    if (kind === "ul") {
      flushList("ol", olItems);
      ulItems.push(stripListPrefix(trimmed));
      continue;
    }
    if (kind === "ol") {
      // Samotna „1. Nagłówek” przed zwykłym tekstem = akapit, nie lista.
      const next = lines.slice(i + 1).find((l) => l.trim());
      const nextKind = next ? listKindOfLine(next.trim()) : null;
      if (olItems.length === 0 && nextKind !== "ol") {
        pushParagraph(trimmed);
        continue;
      }
      flushList("ul", ulItems);
      olItems.push(stripListPrefix(trimmed));
      continue;
    }
    pushParagraph(line);
  }
  flushList("ul", ulItems);
  flushList("ol", olItems);

  if (!segs.some((s) => s.type === "ul" || s.type === "ol")) return null;

  return segs
    .map((s) => {
      if (s.type === "p") return `<p>${escapeHtmlText(s.text)}</p>`;
      const tag = s.type;
      const lis = s.items.map((it) => `<li>${escapeHtmlText(it)}</li>`).join("");
      return `<${tag}>${lis}</${tag}>`;
    })
    .join("");
}

/** HTML z clipboardu (Cursor: div+br) → plain z newlineami. */
export function htmlToPlainWithBreaks(html: string): string {
  if (typeof DOMParser === "undefined") {
    return html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">");
  }
  const parsed = new DOMParser().parseFromString(html, "text/html");
  parsed.body.querySelectorAll("br").forEach((br) => {
    br.replaceWith(parsed.createTextNode("\n"));
  });
  ["p", "div", "li", "h1", "h2", "h3", "h4", "h5", "h6", "tr"].forEach((tag) => {
    parsed.body.querySelectorAll(tag).forEach((el) => {
      el.appendChild(parsed.createTextNode("\n"));
    });
  });
  return (parsed.body.textContent || "").replace(/\n{3,}/g, "\n\n");
}

function isMsoListParagraph(el: Element): boolean {
  const style = el.getAttribute("style") || "";
  const cls = el.getAttribute("class") || "";
  return /mso-list/i.test(style) || /MsoListParagraph/i.test(cls);
}

function isListishBlock(el: Element): boolean {
  const name = el.nodeName;
  if (name !== "P" && name !== "DIV") return false;
  if (isMsoListParagraph(el)) return true;
  const text = (el.textContent || "").replace(/\u00a0/g, " ").trim();
  return listKindOfLine(text) != null;
}

function listKindOfBlock(el: Element): "ul" | "ol" {
  if (isMsoListParagraph(el)) {
    const text = (el.textContent || "").replace(/\u00a0/g, " ").trim();
    return listKindOfLine(text) === "ol" ? "ol" : "ul";
  }
  return listKindOfLine((el.textContent || "").replace(/\u00a0/g, " ").trim()) || "ul";
}

function listItemInnerHtml(el: Element): string {
  if (isMsoListParagraph(el)) {
    // Word często trzyma glyph w pierwszym span; tekst już bez prefiksu.
    const clone = el.cloneNode(true) as HTMLElement;
    clone.querySelectorAll("span[style*='mso-list']").forEach((s) => s.remove());
    const text = (clone.textContent || "").replace(/\u00a0/g, " ").trim();
    if (listKindOfLine(text)) {
      return stripListPrefix(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    }
    return clone.innerHTML.trim() || text;
  }
  const text = (el.textContent || "").replace(/\u00a0/g, " ").trim();
  return stripListPrefix(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Word / Docs: <p>• foo</p> albo MsoListParagraph → <ul>/<ol>.
 * null = zostaw TipTapowi (już ma prawdziwe ul/ol albo brak list).
 */
export function normalizePastedHtmlLists(html: string): string | null {
  if (typeof DOMParser === "undefined") return null;
  const stripped = html.replace(/<!--[\s\S]*?-->/g, "");
  const hasRealList = /<(ul|ol)\b/i.test(stripped);
  const hasFakeHint =
    /mso-list|MsoListParagraph/i.test(stripped) ||
    /<(?:p|div)[^>]*>\s*(?:\u2022|\u2023|•|●|○|[-*+]|\d+[.)])\s+/i.test(stripped);
  if (hasRealList && !hasFakeHint) return null;
  if (!hasFakeHint && !hasRealList) {
    if (!/<(?:p|div)[^>]*>\s*(?:[-*+]|\d+[.)])\s+/i.test(stripped)) return null;
  }

  const parsed = new DOMParser().parseFromString(html, "text/html");
  const body = parsed.body;
  let root: Element = body;
  if (body.children.length === 1 && body.children[0].nodeName === "DIV") {
    root = body.children[0];
  }

  const kids = Array.from(root.childNodes);
  const out: Node[] = [];
  let changed = false;
  let i = 0;
  while (i < kids.length) {
    const node = kids[i];
    if (node.nodeType === 1 && isListishBlock(node as Element)) {
      const kind = listKindOfBlock(node as Element);
      const items: Element[] = [];
      while (
        i < kids.length &&
        kids[i].nodeType === 1 &&
        isListishBlock(kids[i] as Element) &&
        listKindOfBlock(kids[i] as Element) === kind
      ) {
        items.push(kids[i] as Element);
        i++;
      }
      if (items.length >= 1) {
        const list = parsed.createElement(kind === "ol" ? "ol" : "ul");
        for (const el of items) {
          const li = parsed.createElement("li");
          li.innerHTML = listItemInnerHtml(el);
          list.appendChild(li);
        }
        out.push(list);
        changed = true;
        continue;
      }
    }
    out.push(node.cloneNode(true));
    i++;
  }

  if (!changed) return null;
  const wrap = parsed.createElement("div");
  for (const n of out) wrap.appendChild(n);
  return wrap.innerHTML;
}

/**
 * Clipboard → HTML z prawdziwymi <ul>/<ol> do TipTap insertContent.
 * null = nie przejmuj (domyślne wklejenie TipTapa).
 *
 * Cursor: text/plain jest wiarygodniejszy niż HTML (podgląd MD ze stylami).
 * Word: HTML z MsoList / • w <p>. Reszta: HTML→plain→struktura.
 */
export function clipboardListToHtml(data: DataTransfer | null): string | null {
  if (!data) return null;

  const plain = data.getData("text/plain") || "";
  const fromPlain = plainTextToStructuredHtml(plain);
  if (fromPlain) return fromPlain;

  const html = data.getData("text/html") || "";
  if (!html.trim()) return null;

  const stripped = html.replace(/<!--[\s\S]*?-->/g, "");
  const hasReal = /<(ul|ol)\b/i.test(stripped);
  const hasFake =
    /mso-list|MsoListParagraph/i.test(stripped) ||
    /[•●○\u2022\u2023]/.test(stripped) ||
    /<(?:p|div)[^>]*>\s*(?:[-*+]|\d+[.)])\s+/i.test(stripped);
  // Prawdziwe <ul>/<ol> bez fałszywych bulletów → TipTap sam.
  if (hasReal && !hasFake) return null;

  const normalized = normalizePastedHtmlLists(html);
  if (normalized) return normalized;

  // Cursor / VS Code: jeden div z <br> albo spanami → plain + struktura.
  return plainTextToStructuredHtml(htmlToPlainWithBreaks(html));
}

/** Z clipboard: preferuj HTML->MD, potem TSV->MD; null = zostaw domyslne wklejenie. */
export function clipboardToMarkdown(data: DataTransfer | null): string | null {
  if (!data) return null;
  const html = data.getData("text/html") || "";
  if (clipboardHtmlLooksRich(html)) {
    const normalized = normalizePastedHtmlLists(html);
    const md = htmlToMarkdown(normalized || html);
    if (md) return md;
  }
  const plain = data.getData("text/plain") || "";
  if (looksLikePlainList(plain) || hasBulletLines(plain)) {
    return normalizePlainBulletsToMarkdown(plain).trim();
  }
  const tsvHtml = tsvToHtmlTable(plain);
  if (tsvHtml) {
    const md = htmlToMarkdown(tsvHtml);
    if (md) return md;
  }
  return null;
}

/** Wstaw tekst w pozycji kursora textarea / controlled string. */
export function insertAtSelection(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  insert: string,
): { next: string; caret: number } {
  const next = value.slice(0, selectionStart) + insert + value.slice(selectionEnd);
  return { next, caret: selectionStart + insert.length };
}
